import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { tracked } from '@glimmer/tracking';

import { ContractSignWebSocket } from '@repo/customer-registration-kit/contract-sign';
import { Spinner } from '@repo/design-system-kit';
import { dropTask, forever } from 'ember-concurrency';
import { variation } from 'ember-launch-darkly';
import window from 'ember-window-mock';

import { jsURL } from 'register-partners-js/constants/hosts';
import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import { postRegistrationSubmitAsync } from 'register-partners-js/react/registration/queries';
import { fetchNavigationOrganizations } from 'register-partners-js/utils/fetch-navigation-organizations';
import { ignoreCancelation } from 'register-partners-js/utils/ignore-cancelation';

import styles from './styles.module.css';

export default class RegistrationKycSuccessController extends Controller {
  mainLayout = MainLayout;
  spinner = Spinner;
  contractSignWebSocket = ContractSignWebSocket;

  styles = styles;

  @service deviceManager;
  @service router;
  @service networkManager;
  @service intl;
  @service sentry;
  @service toastFlashMessages;
  @service userManager;

  @tracked contractSignWebSocketEnabled = false;

  get websocketToken() {
    return this.userManager.websocketToken;
  }

  get currentUserId() {
    return this.userManager.currentUser?.id;
  }

  submitRegistrationTask = dropTask(async () => {
    if (variation('scaling--async-contract-signature')) {
      await this.submitV2Task.perform().catch(ignoreCancelation);
    } else {
      await this.submitV1Task.perform();
    }
  });

  submitV1Task = dropTask(
    waitFor(async () => {
      await this.model.submit();
      await this.redirectAfterSubmit();
    })
  );

  submitV2Task = dropTask(
    waitFor(async () => {
      try {
        await postRegistrationSubmitAsync(this.model.id);
      } catch (error) {
        this.sentry.captureException(error);
        this.toastFlashMessages.toastError(this.intl.t('errors.internal-server-error'));
        return;
      }

      this.contractSignWebSocketEnabled = true;

      await forever;
    })
  );

  handleContractSignSuccessTask = dropTask(async () => {
    this.submitV2Task.cancelAll();
    await this.redirectAfterSubmit();
  });

  @action
  handleContractSignValidationError() {
    this.submitV2Task.cancelAll();
    this.toastFlashMessages.toastError(this.intl.t('errors.internal-server-error'));
  }

  @action
  handleContractSignTimeout() {
    this.submitV2Task.cancelAll();
    this.router.transitionTo('registration.pending-contract');
  }

  async redirectAfterSubmit() {
    let organizationID = this.model.belongsTo('organization').id();

    if (this.deviceManager.isMobile || this.deviceManager.isWebview) {
      await this.router.transitionTo('registrations.already-created', organizationID);
    } else {
      let organizations = await fetchNavigationOrganizations(this.networkManager);
      let organization = organizations.find(org => org.id === organizationID);
      window.location.href = `${jsURL}/organizations/${organization.slug}`;
    }
  }
}
